
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function ContactSection() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyType: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('propertyType', formData.propertyType);
      body.append('message', formData.message);

      const res = await fetch('https://readdy.ai/api/form/d6h4ffhtp4hj31tchve0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', propertyType: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const infoItems = t('managementContact.infoItems', { returnObjects: true }) as string[];
  const recommendItems = t('managementContact.recommendItems', { returnObjects: true }) as string[];
  const infoIcons = ['ri-time-line', 'ri-map-pin-line', 'ri-reply-line', 'ri-shield-check-line'];

  return (
    <section id="contact" className="py-24 relative overflow-hidden">
      {/* 和紙風背景 */}
      <div className="absolute inset-0 bg-gradient-to-br from-stone-900 via-slate-800 to-stone-900" />
      <div
        className="absolute inset-0 bg-cover bg-center opacity-10"
        style={{ backgroundImage: `url('https://public.readdy.ai/ai/img_res/496a48f66f59449d00251fde76c0893a.jpg')` }}
      />

      {/* 上部装飾ライン */}
      <div className="absolute top-0 left-0 right-0 h-3 bg-gradient-to-r from-transparent via-[#8b2500] to-transparent opacity-80" />
      <div className="absolute top-3 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" />

      {/* 市松模様装飾 */}
      <div className="absolute top-8 left-0 right-0 flex justify-center gap-1">
        {[...Array(20)].map((_, i) => (
          <div key={i} className={`w-3 h-3 ${i % 2 === 0 ? 'bg-[#8b2500]' : 'bg-[#c9a84c]'} opacity-60`} />
        ))}
      </div>

      <div className="absolute -left-32 -top-32 w-96 h-96 rounded-full bg-[#c9a84c]/10" />
      <div className="absolute -right-32 -bottom-32 w-96 h-96 rounded-full bg-[#c9a84c]/10" />

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 bg-[#8b2500]/80 text-[#c9a84c] text-xs font-bold tracking-widest uppercase px-5 py-2 rounded-full mb-4 border border-[#c9a84c]/30" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            <i className="ri-mail-line text-base"></i>
            Contact
          </span>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            <span className="text-[#c9a84c]">◆</span> {t('contact.title')} <span className="text-[#c9a84c]">◆</span>
          </h2>
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#c9a84c]" />
            <span className="text-[#c9a84c] text-sm" style={{ fontFamily: "'Noto Serif JP', serif" }}>✦</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#c9a84c]" />
          </div>
          <p className="text-lg text-slate-300" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-10 items-start">
          {/* Left info */}
          <div className="md:col-span-2 space-y-5">
            <div className="bg-white/8 backdrop-blur-sm border-2 border-[#c9a84c]/30 rounded-2xl p-7 relative">
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#c9a84c]" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#c9a84c]" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#c9a84c]" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#c9a84c]" />

              <h3 className="text-lg font-bold text-white mb-5 flex items-center gap-3" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                <div className="w-9 h-9 flex items-center justify-center bg-gradient-to-br from-[#8b2500] to-[#a52a00] rounded-xl flex-shrink-0 shadow-lg">
                  <i className="ri-information-line text-white text-lg"></i>
                </div>
                {t('managementContact.beforeContact')}
              </h3>
              <ul className="space-y-4">
                {infoItems.map((text, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#8b2500]/60 rounded-lg flex-shrink-0 border border-[#c9a84c]/30">
                      <i className={`${infoIcons[i] ?? 'ri-check-line'} text-[#c9a84c] text-base`}></i>
                    </div>
                    <span className="text-slate-300 text-sm" style={{ fontFamily: "'Noto Serif JP', serif" }}>{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white/8 backdrop-blur-sm border-2 border-[#c9a84c]/30 rounded-2xl p-7 relative">
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#c9a84c]" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#c9a84c]" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#c9a84c]" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#c9a84c]" />

              <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-[#8b2500] to-[#a52a00] rounded-xl mb-4 shadow-lg">
                <i className="ri-question-answer-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-white mb-3" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                {t('managementContact.recommendTitle')}
              </h3>
              <ul className="space-y-2 text-sm text-slate-300" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                {recommendItems.map((text, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <i className="ri-arrow-right-s-line text-[#c9a84c] flex-shrink-0"></i>
                    {text}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Form */}
          <div className="md:col-span-3 bg-gradient-to-br from-amber-50 via-stone-50 to-amber-50 rounded-2xl shadow-2xl p-8 md:p-10 border-4 border-[#8b2500] relative">
            <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-[#c9a84c]" />
            <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-[#c9a84c]" />
            <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-[#c9a84c]" />
            <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-[#c9a84c]" />

            <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              <div className="w-9 h-9 flex items-center justify-center bg-gradient-to-br from-[#8b2500] to-[#a52a00] rounded-xl shadow-lg">
                <i className="ri-send-plane-line text-white text-lg"></i>
              </div>
              {t('managementContact.title')}
            </h3>
            <form
              id="management-contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                    <span>{t('managementContact.name')}</span>
                    <span className="bg-[#8b2500] text-white text-[10px] px-2 py-0.5 rounded">{t('managementContact.nameRequired')}</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#8b2500] rounded-l-lg" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="山田 太郎"
                      className="w-full pl-5 pr-4 py-3 rounded-lg border-2 border-slate-200 focus:border-[#8b2500] focus:ring-2 focus:ring-[#8b2500]/20 outline-none transition-all text-sm bg-white"
                      style={{ fontFamily: "'Noto Serif JP', serif" }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                    {t('managementContact.phone')}
                  </label>
                  <div className="relative">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#c9a84c] rounded-l-lg" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="090-1234-5678"
                      className="w-full pl-5 pr-4 py-3 rounded-lg border-2 border-slate-200 focus:border-[#8b2500] focus:ring-2 focus:ring-[#8b2500]/20 outline-none transition-all text-sm bg-white"
                      style={{ fontFamily: "'Noto Serif JP', serif" }}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  <span>{t('managementContact.email')}</span>
                  <span className="bg-[#8b2500] text-white text-[10px] px-2 py-0.5 rounded">{t('managementContact.emailRequired')}</span>
                </label>
                <div className="relative">
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#8b2500] rounded-l-lg" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="example@email.com"
                    className="w-full pl-5 pr-4 py-3 rounded-lg border-2 border-slate-200 focus:border-[#8b2500] focus:ring-2 focus:ring-[#8b2500]/20 outline-none transition-all text-sm bg-white"
                    style={{ fontFamily: "'Noto Serif JP', serif" }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  {t('managementContact.propertyType')}
                </label>
                <div className="relative">
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#c9a84c] rounded-l-lg" />
                  <select
                    name="propertyType"
                    value={formData.propertyType}
                    onChange={handleChange}
                    className="w-full pl-5 pr-4 py-3 rounded-lg border-2 border-slate-200 focus:border-[#8b2500] focus:ring-2 focus:ring-[#8b2500]/20 outline-none transition-all text-sm bg-white cursor-pointer appearance-none"
                    style={{ fontFamily: "'Noto Serif JP', serif", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23c9a84c' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em' }}
                  >
                    <option value="">{t('managementContact.propertyTypePlaceholder')}</option>
                    <option value={t('managementContact.propertyTypeOptions.mansion1k')}>{t('managementContact.propertyTypeOptions.mansion1k')}</option>
                    <option value={t('managementContact.propertyTypeOptions.mansion2ldk')}>{t('managementContact.propertyTypeOptions.mansion2ldk')}</option>
                    <option value={t('managementContact.propertyTypeOptions.house')}>{t('managementContact.propertyTypeOptions.house')}</option>
                    <option value={t('managementContact.propertyTypeOptions.kominka')}>{t('managementContact.propertyTypeOptions.kominka')}</option>
                    <option value={t('managementContact.propertyTypeOptions.taxConsult')}>{t('managementContact.propertyTypeOptions.taxConsult')}</option>
                    <option value={t('managementContact.propertyTypeOptions.other')}>{t('managementContact.propertyTypeOptions.other')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  <span>{t('managementContact.consultContent')}</span>
                  <div className="flex items-center gap-2">
                    <span className="bg-[#8b2500] text-white text-[10px] px-2 py-0.5 rounded">{t('managementContact.consultContentRequired')}</span>
                    <span className={`text-[10px] ${formData.message.length > 500 ? 'text-red-500' : 'text-slate-400'}`}>
                      {formData.message.length}/500
                    </span>
                  </div>
                </label>
                <div className="relative">
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#8b2500] rounded-l-lg" />
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    placeholder={t('managementContact.consultContentPlaceholder')}
                    className="w-full pl-5 pr-4 py-3 rounded-lg border-2 border-slate-200 focus:border-[#8b2500] focus:ring-2 focus:ring-[#8b2500]/20 outline-none transition-all resize-none text-sm bg-white"
                    style={{ fontFamily: "'Noto Serif JP', serif" }}
                  />
                </div>
              </div>

              <div className="h-px bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" />

              <button
                type="submit"
                disabled={isSubmitting || formData.message.length > 500}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#8b2500] via-[#a52a00] to-[#8b2500] hover:from-[#a52a00] hover:via-[#8b2500] hover:to-[#a52a00] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold py-4 rounded-lg transition-all hover:shadow-xl hover:shadow-[#8b2500]/30 hover:-translate-y-0.5 whitespace-nowrap shadow-lg"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                <span className="text-[#c9a84c]">✦</span>
                <i className="ri-mail-send-line text-lg"></i>
                {isSubmitting ? t('managementContact.submitting') : t('managementContact.submit')}
                <span className="text-[#c9a84c]">✦</span>
              </button>

              <div className="h-px bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" />

              <p className="text-center text-xs text-slate-500" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                {t('managementContact.confirmNote')}
              </p>

              {/* お支払い方法 */}
              <div
                className="rounded-xl p-4"
                style={{ background: 'linear-gradient(135deg, #fff8e1 0%, #fef3e2 100%)', border: '2px solid #c9a84c' }}
              >
                <p className="text-xs font-bold text-center mb-3" style={{ color: '#8b2500', fontFamily: "'Noto Serif JP', serif" }}>
                  ◆ ご利用可能なお支払い方法 ◆
                </p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <div className="flex items-center justify-center px-3 py-1.5 rounded-md bg-white shadow-sm border border-gray-200" style={{ minWidth: 56 }}>
                    <span className="font-extrabold text-sm tracking-tight" style={{ color: '#1A1F71', fontFamily: 'Arial, sans-serif' }}>VISA</span>
                  </div>
                  <div className="flex items-center justify-center gap-1 px-3 py-1.5 rounded-md bg-white shadow-sm border border-gray-200" style={{ minWidth: 56 }}>
                    <div className="w-5 h-5 rounded-full" style={{ background: '#EB001B' }}></div>
                    <div className="w-5 h-5 rounded-full -ml-2.5" style={{ background: '#F79E1B', opacity: 0.9 }}></div>
                  </div>
                  <div className="flex items-center justify-center px-3 py-1.5 rounded-md bg-white shadow-sm border border-gray-200" style={{ minWidth: 56 }}>
                    <span className="font-extrabold text-sm" style={{ background: 'linear-gradient(135deg, #003087, #009f6b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontFamily: 'Arial, sans-serif' }}>JCB</span>
                  </div>
                  <div className="flex items-center justify-center px-3 py-1.5 rounded-md bg-white shadow-sm border border-gray-200" style={{ minWidth: 56 }}>
                    <span className="font-extrabold text-xs tracking-wide" style={{ color: '#007BC1', fontFamily: 'Arial, sans-serif' }}>AMEX</span>
                  </div>
                </div>
                <p className="text-xs text-center mt-2" style={{ color: '#9a7a50', fontFamily: "'Noto Serif JP', serif" }}>
                  クレジットカード決済に対応しています
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className="flex items-start gap-3 bg-emerald-50 border-2 border-emerald-500 text-emerald-800 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-checkbox-circle-line text-emerald-500 text-lg flex-shrink-0 mt-0.5"></i>
                  <span style={{ fontFamily: "'Noto Serif JP', serif" }}>{t('managementContact.success')}</span>
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="flex items-start gap-3 bg-red-50 border-2 border-red-500 text-red-800 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0 mt-0.5"></i>
                  <span style={{ fontFamily: "'Noto Serif JP', serif" }}>{t('managementContact.error')}</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* 下部装飾ライン */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-1">
        {[...Array(20)].map((_, i) => (
          <div key={i} className={`w-3 h-3 ${i % 2 === 0 ? 'bg-[#8b2500]' : 'bg-[#c9a84c]'} opacity-60`} />
        ))}
      </div>
      <div className="absolute bottom-4 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-3 bg-gradient-to-r from-transparent via-[#8b2500] to-transparent opacity-80" />
    </section>
  );
}